import {
  EditableField,
  UnsavedChanges,
  PendingStatusConfirm,
  BulkEditingContextValue,
  GetEffortRequestsFormParams,
  UpdateEffortRequestPostFormParams,
} from './types';
import {
  ChipsArray,
  SummaryCell,
  DataGridBox,
  CustomToolbar,
  ZebraDataGrid,
  GridEditDateCell,
  PermissionHandler,
  CustomNoRowsOverlay,
  GridFilterDateInput,
} from 'components';
import {
  StatusChip,
  DetailDrawer,
  InvoiceNoEditCell,
  CompanyVknEditCell,
  StatusRollbackDialog,
  CreateEffortRequestDialog,
  DeleteEffortRequestDialog,
  AppointedConsultantEditCell,
} from './components';
import dayjs from 'dayjs';
import {
  MuiEvent,
  GridRowId,
  GridColDef,
  gridClasses,
  useGridApiRef,
  GridColTypeDef,
  GRID_DATE_COL_DEF,
  isAutogeneratedRow,
  GridActionsCellItem,
  getGridDateOperators,
  DataGridPremiumProps,
  GridRowSelectionModel,
  GridCellEditStartParams,
} from '@mui/x-data-grid-premium';
import { Book } from 'pages/dashboard';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import projectsActions from 'store/features/projects/actions';
import { EffortItem, User, ActivityType } from 'library/types';
import { Button, darken, Grid2 as Grid, Tooltip } from '@mui/material';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { selectPermissionCodes } from 'store/features/permissions/selector';
import CreateActivityBoardItemDialog, {
  InitialActivityData,
} from 'pages/activity-managing/components/create-activity-board-item-dialog';
import { Authentication, renderDataGridLocaleText, renderFormattedDate } from 'utils';
import { selectProjects, selectProjectsIsEmpty } from 'store/features/projects/selector';
import { EFFORT_STATUS_TYPE_ENUM, STORAGE_KEY_ENUM, TABLE_SCHEMA_PAGE_NAME_ENUM } from 'enums';
import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const PERM = {
  SALES: 'update-effort-sales-group',
  INVOICE: 'update-effort-invoice-group',
  USER: 'update-effort-user-group',
  ADMIN: 'update-effort-admin',
} as const;

type PermCode = (typeof PERM)[keyof typeof PERM];

const EDITABLE_FIELDS_BY_PERMISSION: Record<PermCode, ReadonlySet<EditableField>> = {
  [PERM.SALES]: new Set<EditableField>([
    'companyVkn',
    'isSasNecessary',
    'sasNo',
    'invoiceNote',
    'companyVknForInvoice',
  ]),
  [PERM.INVOICE]: new Set<EditableField>(['companyVkn', 'invoiceNo', 'invoiceDate', 'companyVknForInvoice']),
  [PERM.USER]: new Set<EditableField>(['companyVkn', 'appointedConsultantId', 'consultantNote']),
  [PERM.ADMIN]: new Set<EditableField>([
    'companyVkn',
    'appointedConsultantId',
    'consultantNote',
    'isSasNecessary',
    'sasNo',
    'invoiceNote',
    'companyVknForInvoice',
    'invoiceNo',
    'invoiceDate',
  ]),
};

const STATUS_TRANSITIONS_BY_PERMISSION: Record<PermCode, Array<[EFFORT_STATUS_TYPE_ENUM, EFFORT_STATUS_TYPE_ENUM]>> = {
  [PERM.SALES]: [
    [EFFORT_STATUS_TYPE_ENUM.IN_SALES, EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL],
    [EFFORT_STATUS_TYPE_ENUM.APPROVED, EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL],
    [EFFORT_STATUS_TYPE_ENUM.REJECTED, EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL],
  ],
  [PERM.INVOICE]: [[EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED, EFFORT_STATUS_TYPE_ENUM.INVOICED]],
  [PERM.USER]: [
    [EFFORT_STATUS_TYPE_ENUM.APPROVED, EFFORT_STATUS_TYPE_ENUM.WORK_STARTED],
    [EFFORT_STATUS_TYPE_ENUM.WORK_STARTED, EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER],
    [EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER, EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED],
    [EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED, EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER],
    [EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER, EFFORT_STATUS_TYPE_ENUM.WORK_STARTED],
  ],
  [PERM.ADMIN]: [],
};

const hasPerm = (permissionCodes: string[], perm: PermCode) => permissionCodes.includes(perm);

const isKnownEditableField = (field: EditableField) =>
  EDITABLE_FIELDS_BY_PERMISSION[PERM.SALES].has(field) ||
  EDITABLE_FIELDS_BY_PERMISSION[PERM.INVOICE].has(field) ||
  EDITABLE_FIELDS_BY_PERMISSION[PERM.USER].has(field) ||
  EDITABLE_FIELDS_BY_PERMISSION[PERM.ADMIN].has(field);

const canEditFieldByPermission = (permissionCodes: string[], field: EditableField) => {
  if (hasPerm(permissionCodes, PERM.ADMIN)) return true;
  if (!isKnownEditableField(field)) return false;

  return (
    (hasPerm(permissionCodes, PERM.SALES) && EDITABLE_FIELDS_BY_PERMISSION[PERM.SALES].has(field)) ||
    (hasPerm(permissionCodes, PERM.INVOICE) && EDITABLE_FIELDS_BY_PERMISSION[PERM.INVOICE].has(field)) ||
    (hasPerm(permissionCodes, PERM.USER) && EDITABLE_FIELDS_BY_PERMISSION[PERM.USER].has(field))
  );
};

const canTransitionStatusByPermission = (
  permissionCodes: string[],
  from: EFFORT_STATUS_TYPE_ENUM,
  to: EFFORT_STATUS_TYPE_ENUM
) => {
  if (hasPerm(permissionCodes, PERM.ADMIN)) return true;

  if (from === EFFORT_STATUS_TYPE_ENUM.INVOICED && to !== EFFORT_STATUS_TYPE_ENUM.INVOICED) return false;

  const checks: PermCode[] = [];
  if (hasPerm(permissionCodes, PERM.SALES)) checks.push(PERM.SALES);
  if (hasPerm(permissionCodes, PERM.INVOICE)) checks.push(PERM.INVOICE);
  if (hasPerm(permissionCodes, PERM.USER)) checks.push(PERM.USER);

  return checks.some((p) => STATUS_TRANSITIONS_BY_PERMISSION[p].some(([f, t]) => f === from && t === to));
};

const dateColumnType: GridColTypeDef<Date, string> = {
  ...GRID_DATE_COL_DEF,
  resizable: false,
  renderEditCell: (params) => {
    const v = params.value;
    const dayjsValue = v instanceof Date ? dayjs(v) : v ? dayjs(v) : null;
    return <GridEditDateCell {...params} value={dayjsValue} />;
  },
  filterOperators: getGridDateOperators(false).map((item) => ({
    ...item,
    InputComponent: GridFilterDateInput,
    InputComponentProps: { showTime: false },
  })),
};

const BulkEditingContext = createContext<BulkEditingContextValue | null>(null);

const EffortTracing = () => {
  // other hooks
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const lastEditWarnAtRef = useRef<number>(0);
  const unsavedChangesRef = useRef<UnsavedChanges>({
    unsavedRows: {},
    rowsBeforeChange: {},
  });

  // redux hooks
  const dispatch = useAppDispatch();
  const products = useAppSelector(selectProjects);
  const pageName = TABLE_SCHEMA_PAGE_NAME_ENUM.EFFORT_TRACING;
  const permissionCodes = useAppSelector(selectPermissionCodes);
  const isEmptyProducts = useAppSelector(selectProjectsIsEmpty);

  // useState hooks
  const [loading, setLoading] = useState(false);
  const [books, setBooks] = useState<Book[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [efforts, setEfforts] = useState<EffortItem[]>([]);
  const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [statusType, setStatusType] = useState<number | null>(null);
  const [openActivityDialog, setOpenActivityDialog] = useState(false);
  const [detailRow, setDetailRow] = useState<EffortItem | null>(null);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [showOnlyAssignedToMe, setShowOnlyAssignedToMe] = useState(false);
  const [openStatusRollbackDialog, setOpenStatusRollbackDialog] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [openCreateEffortRequestDialog, setOpenCreateEffortRequestDialog] = useState(false);
  const [openDeleteEffortRequestDialog, setOpenDeleteEffortRequestDialog] = useState(false);
  const [initialActivityData, setInitialActivityData] = useState<InitialActivityData | null>(null);
  const [statusRollbackPayload, setStatusRollbackPayload] = useState<{ fromLabel: string; toLabel: string } | null>(
    null
  );

  const canAnyUpdate = useMemo(() => {
    return (
      hasPerm(permissionCodes, PERM.ADMIN) ||
      hasPerm(permissionCodes, PERM.SALES) ||
      hasPerm(permissionCodes, PERM.INVOICE) ||
      hasPerm(permissionCodes, PERM.USER)
    );
  }, [permissionCodes]);

  const filteredActivityTypes = useMemo(() => {
    const me = users.find((user) => user.nameSurname === Authentication.getLoggedUser());

    if (!me?.roleId) return activityTypes;

    return activityTypes.filter((at) => at.roles.some((role) => role.id === me.roleId));
  }, [users, activityTypes]);

  const warnLockedEdit = useCallback(() => {
    const now = Date.now();
    if (now - lastEditWarnAtRef.current < 800) return;

    lastEditWarnAtRef.current = now;

    enqueueSnackbar(
      <FormattedMessage
        id="effortTracing.singleRowEditLock"
        defaultMessage="Kaydedilmemiş değişiklik var. Lütfen önce mevcut satırı kaydedin veya iptal edin."
      />,
      { variant: 'warning' }
    );
  }, [enqueueSnackbar]);

  const openRowDetail = useCallback(
    (row: EffortItem) => {
      if (hasUnsavedRows) {
        warnLockedEdit();
        return;
      }

      setDetailRow(row);
      setOpenDetail(true);
    },
    [hasUnsavedRows, warnLockedEdit]
  );

  const handleAddActivity = useCallback(
    (row: EffortItem) => {
      if (hasUnsavedRows) {
        warnLockedEdit();
        return;
      }

      if (row.status === EFFORT_STATUS_TYPE_ENUM.INVOICED) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'effortTracing.cannotAddActivityToInvoiced',
            defaultMessage: 'Faturası Kesilmiş Efor Talepleri için Aktivite Girişi yapılamaz',
          }),
          { variant: 'warning' }
        );
        return;
      }

      setInitialActivityData({
        bookId: row.bookId,
        projectId: row.projectId,
        effortTrackNo: row.trackNo,
      });
      setOpenActivityDialog(true);
    },
    [hasUnsavedRows, warnLockedEdit, enqueueSnackbar, intl]
  );

  const getEffortStatusTypeTranslated = useCallback(
    (effortStatusType: number) =>
      ({
        [EFFORT_STATUS_TYPE_ENUM.IN_SALES]: intl.formatMessage({
          id: 'enum.effortStatusType.inSales',
          defaultMessage: 'Satış Ekibinde',
        }),
        [EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL]: intl.formatMessage({
          id: 'enum.effortStatusType.pendingCustomerApproval',
          defaultMessage: 'Müşteri Onayında',
        }),
        [EFFORT_STATUS_TYPE_ENUM.APPROVED]: intl.formatMessage({
          id: 'enum.effortStatusType.approved',
          defaultMessage: 'Efor Onaylandı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.REJECTED]: intl.formatMessage({
          id: 'enum.effortStatusType.rejected',
          defaultMessage: 'Efor Reddedildi',
        }),
        [EFFORT_STATUS_TYPE_ENUM.WORK_STARTED]: intl.formatMessage({
          id: 'enum.effortStatusType.workStarted',
          defaultMessage: 'Çalışma Başlatıldı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER]: intl.formatMessage({
          id: 'enum.effortStatusType.testingByCustomer',
          defaultMessage: 'Müşteri Testinde',
        }),
        [EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED]: intl.formatMessage({
          id: 'enum.effortStatusType.workCompleted',
          defaultMessage: 'Çalışma Tamamlandı',
        }),
        [EFFORT_STATUS_TYPE_ENUM.INVOICED]: intl.formatMessage({
          id: 'enum.effortStatusType.invoiced',
          defaultMessage: 'Faturası Kesildi',
        }),
      })[effortStatusType],
    [intl]
  );

  const STATUS_OPTIONS = useMemo(
    () => [
      {
        value: EFFORT_STATUS_TYPE_ENUM.IN_SALES,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.IN_SALES),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.APPROVED,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.APPROVED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.REJECTED,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.REJECTED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.WORK_STARTED,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.WORK_STARTED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.INVOICED,
        label: getEffortStatusTypeTranslated(EFFORT_STATUS_TYPE_ENUM.INVOICED),
      },
    ],
    [getEffortStatusTypeTranslated]
  );

  const statusRank = useMemo(() => {
    const map = new Map<number, number>();
    STATUS_OPTIONS.forEach((opt, i) => map.set(opt.value, i));

    return map;
  }, [STATUS_OPTIONS]);

  const pendingStatusConfirmRef = useRef<PendingStatusConfirm | null>(null);

  const isBackwardStatusChange = useCallback(
    (fromStatus: number, toStatus: number) => {
      const from = statusRank.get(fromStatus) ?? -1;
      const to = statusRank.get(toStatus) ?? -1;

      return to < from;
    },
    [statusRank]
  );

  const getStatusLabel = useCallback(
    (v: number) => STATUS_OPTIONS.find((x) => x.value === v)?.label ?? String(v),
    [STATUS_OPTIONS]
  );

  const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

  const applyRowUpdate = useCallback(
    (newRow: EffortItem, oldRow: EffortItem) => {
      const rowId = newRow.id;

      const keys = Object.keys(unsavedChangesRef.current.unsavedRows);
      const lockedId = keys.length ? (keys[0] as GridRowId) : null;

      if (lockedId && Number(lockedId) !== rowId) {
        warnLockedEdit();
        return oldRow;
      }

      unsavedChangesRef.current.unsavedRows[rowId] = newRow;
      if (!unsavedChangesRef.current.rowsBeforeChange[rowId]) {
        unsavedChangesRef.current.rowsBeforeChange[rowId] = oldRow;
      }
      setHasUnsavedRows(true);

      return newRow;
    },
    [warnLockedEdit]
  );

  const getRowClassName = useCallback<NonNullable<DataGridPremiumProps['getRowClassName']>>(
    ({ id }) => {
      const unsavedRow = unsavedChangesRef.current.unsavedRows[id];

      if (unsavedRow) {
        if ((unsavedRow as any)._action === 'delete') return 'row--removed row--locked';
        return 'row--edited row--locked';
      }

      if (hasUnsavedRows) {
        const lockedId = Object.keys(unsavedChangesRef.current.unsavedRows)[0];
        if (lockedId && id !== Number(lockedId)) {
          return 'row--disabled';
        }
      }

      return '';
    },
    [hasUnsavedRows]
  );

  const bulkEditingContextValue = useMemo<BulkEditingContextValue>(
    () => ({
      unsavedChangesRef,
      setHasUnsavedRows,
    }),
    []
  );

  const getEditDenyMessage = useCallback(
    (params: { id: GridRowId; field: string; row: any; colDef: any }) => {
      if (hasUnsavedRows) {
        const lockedKey = Object.keys(unsavedChangesRef.current.unsavedRows)[0];
        const lockedId = lockedKey ? (Number(lockedKey) as GridRowId) : null;

        if (lockedId != null && params.id !== lockedId) {
          return intl.formatMessage({
            id: 'effortTracing.unsavedChangesLock',
            defaultMessage: 'Kaydedilmemiş değişiklik var. Lütfen önce mevcut satırı kaydedin veya iptal edin!',
          });
        }
      }

      const field = params.field as EditableField;

      if (field === 'status') {
        if (!canAnyUpdate) {
          return intl.formatMessage({
            id: 'effortTracing.noPermissionToUpdateStatus',
            defaultMessage: 'Statü güncelleme yetkiniz yok.',
          });
        }

        return null;
      }

      if (!params.colDef.editable) {
        return intl.formatMessage({
          id: 'effortTracing.notEditableField',
          defaultMessage: 'Bu alan düzenlenebilir değil!',
        });
      }

      if (!canEditFieldByPermission(permissionCodes, field)) {
        return intl.formatMessage({
          id: 'effortTracing.noPermissionToEditField',
          defaultMessage: 'Bu alanı güncelleme yetkiniz yok.',
        });
      }

      return null;
    },
    [hasUnsavedRows, intl, permissionCodes, canAnyUpdate]
  );

  const isCellEditable = useCallback<NonNullable<DataGridPremiumProps['isCellEditable']>>(
    (params) => !getEditDenyMessage(params as any),
    [getEditDenyMessage]
  );

  const handleCellEditStart = useCallback(
    (params: GridCellEditStartParams, event: MuiEvent) => {
      if (hasUnsavedRows) {
        const lockedId = Object.keys(unsavedChangesRef.current.unsavedRows)[0];

        if (lockedId && params.id !== Number(lockedId)) {
          event.defaultMuiPrevented = true;
          warnLockedEdit();
          return;
        }
      }

      const msg = getEditDenyMessage(params as any);

      if (msg) {
        event.defaultMuiPrevented = true;
      }
    },
    [hasUnsavedRows, warnLockedEdit, getEditDenyMessage]
  );

  const handleCellDoubleClick = useCallback<NonNullable<DataGridPremiumProps['onCellDoubleClick']>>(
    (params, event) => {
      const msg = getEditDenyMessage(params as any);

      if (msg) {
        (event as MuiEvent).defaultMuiPrevented = true;
        enqueueSnackbar(msg, { variant: 'warning' });
      }
    },
    [getEditDenyMessage, enqueueSnackbar]
  );

  const validateEffortBeforeSave = useCallback((row: EffortItem, intl: IntlShape, permissionCodesAny: string[]) => {
    const errors: string[] = [];
    const canEdit = (f: EditableField) => canEditFieldByPermission(permissionCodesAny, f);

    if (canEdit('companyVkn')) {
      const companyVkn = (row.companyVkn ?? '').toString().trim();

      if (!companyVkn.length) {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.companyVknRequired',
            defaultMessage: 'Talep eden şirket boş bırakılamaz.',
          })
        );
      }
    }

    if (canEdit('appointedConsultantId')) {
      if (row.appointedConsultantId == null) {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.appointedConsultantRequired',
            defaultMessage: 'Atanan danışman boş bırakılamaz.',
          })
        );
      }

      if (typeof row.appointedConsultantId === 'string') {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.appointedConsultantInvalidType',
            defaultMessage: 'Atanan danışman geçersiz (ID sayı olmalı).',
          })
        );
      }
    }

    if (canEdit('isSasNecessary') || canEdit('sasNo')) {
      const sasNecessary = Boolean(row.isSasNecessary);

      if (sasNecessary && canEdit('sasNo')) {
        if (!isNonEmptyString(row.sasNo)) {
          errors.push(
            intl.formatMessage({
              id: 'effortTracing.validation.sasNoRequiredWhenNecessary',
              defaultMessage: 'SAS zorunlu ise SAS No boş bırakılamaz.',
            })
          );
        }
      }
    }

    if (canEdit('companyVknForInvoice')) {
      const vkn = (row.companyVknForInvoice ?? '').toString().trim();

      if (!vkn.length) {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.companyVknForInvoiceRequired',
            defaultMessage: 'Fatura kesilecek VKN boş bırakılamaz.',
          })
        );
      } else if (!/^\d{10}$/.test(vkn)) {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.companyVknForInvoiceInvalid',
            defaultMessage: 'Fatura kesilecek VKN 10 haneli ve sadece rakamlardan oluşmalıdır.',
          })
        );
      }
    }

    if (row.status === EFFORT_STATUS_TYPE_ENUM.INVOICED) {
      if (canEdit('invoiceNo') && !isNonEmptyString(row.invoiceNo)) {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.invoiceNoRequiredWhenInvoiced',
            defaultMessage: 'Faturası kesilen taleplerde fatura numarası zorunludur.',
          })
        );
      }

      if (canEdit('invoiceDate') && !row.invoiceDate) {
        errors.push(
          intl.formatMessage({
            id: 'effortTracing.validation.invoiceDateRequiredWhenInvoiced',
            defaultMessage: 'Faturası kesilen taleplerde fatura tarihi zorunludur.',
          })
        );
      }
    }

    if (canEdit('invoiceNo') && isNonEmptyString(row.invoiceNo) && row.invoiceNo.trim().length > 16) {
      errors.push(
        intl.formatMessage({
          id: 'effortTracing.validation.invoiceNoMax16',
          defaultMessage: 'Fatura No en fazla 16 karakter olabilir.',
        })
      );
    }

    return errors;
  }, []);

  const toNullableString = (v: unknown): string | null => {
    if (typeof v !== 'string') return null;

    const s = v.trim();

    return s.length ? s : null;
  };

  const toNullableNumber = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;

    return null;
  };

  const toBoolean = (v: unknown): boolean => Boolean(v);

  const toNullableIso = (v: unknown): string | null => {
    if (!v) return null;

    const d = dayjs(v as any);

    return d.isValid() ? d.toISOString() : null;
  };

  const buildUpdatePayload = useCallback(
    (newRow: EffortItem, beforeRow: EffortItem, permissionCodesAny: string[]): UpdateEffortRequestPostFormParams => {
      const canEdit = (f: EditableField) => canEditFieldByPermission(permissionCodesAny, f);

      const pick = <K extends keyof EffortItem>(field: K, editableField: EditableField): EffortItem[K] => {
        return canEdit(editableField) ? newRow[field] : beforeRow[field];
      };

      const appointedConsultantIdRaw = pick('appointedConsultantId', 'appointedConsultantId');
      const consultantNoteRaw = pick('consultantNote', 'consultantNote');

      const isSasNecessaryRaw = pick('isSasNecessary', 'isSasNecessary');
      const sasNoRaw = pick('sasNo', 'sasNo');
      const invoiceNoteRaw = pick('invoiceNote', 'invoiceNote');

      const companyVknRaw = pick('companyVkn', 'companyVkn');
      const companyVknForInvoiceRaw = pick('companyVknForInvoice', 'companyVknForInvoice');

      const invoiceNoRaw = pick('invoiceNo', 'invoiceNo');
      const invoiceDateRaw = pick('invoiceDate', 'invoiceDate');

      return {
        id: newRow.id,
        status: newRow.status as EFFORT_STATUS_TYPE_ENUM,

        appointedConsultantId: toNullableNumber(appointedConsultantIdRaw),
        consultantNote: toNullableString(consultantNoteRaw),

        isSasNecessary: toBoolean(isSasNecessaryRaw),
        sasNo: toNullableString(sasNoRaw),
        invoiceNote: toNullableString(invoiceNoteRaw),

        companyVkn: toNullableString(companyVknRaw),
        companyVknForInvoice: toNullableString(companyVknForInvoiceRaw),

        invoiceNo: toNullableString(invoiceNoRaw),
        invoiceDate: toNullableIso(invoiceDateRaw),
      };
    },
    []
  );

  const processRowUpdate = useCallback<NonNullable<DataGridPremiumProps['processRowUpdate']>>(
    (newRowAny, oldRowAny) => {
      const newRow = newRowAny as EffortItem;
      const oldRow = oldRowAny as EffortItem;

      const fromStatus = Number(oldRow.status) as EFFORT_STATUS_TYPE_ENUM;
      const toStatus = Number(newRow.status) as EFFORT_STATUS_TYPE_ENUM;

      if (fromStatus !== toStatus) {
        const allowed = canTransitionStatusByPermission(permissionCodes, fromStatus, toStatus);

        if (!allowed) {
          enqueueSnackbar(
            intl.formatMessage({
              id: 'effortTracing.statusTransitionNotAllowed',
              defaultMessage: 'Bu statü değişikliğine yetkiniz yok.',
            }),
            { variant: 'warning' }
          );

          return applyRowUpdate({ ...newRow, status: oldRow.status }, oldRow);
        }
      }

      const isStatusChanged = fromStatus !== toStatus;
      const shouldConfirm = isStatusChanged && isBackwardStatusChange(fromStatus, toStatus);

      if (!shouldConfirm) {
        return applyRowUpdate(newRow, oldRow);
      }

      return new Promise<EffortItem>((resolve) => {
        pendingStatusConfirmRef.current = { newRow, oldRow, resolve };

        setStatusRollbackPayload({
          fromLabel: getStatusLabel(fromStatus),
          toLabel: getStatusLabel(toStatus),
        });
        setOpenStatusRollbackDialog(true);
      });
    },
    [permissionCodes, enqueueSnackbar, intl, applyRowUpdate, isBackwardStatusChange, getStatusLabel]
  );

  const handleStatusRollbackCancel = useCallback(() => {
    const pending = pendingStatusConfirmRef.current;
    if (!pending) return;

    pending.resolve(pending.oldRow);

    pendingStatusConfirmRef.current = null;
    setOpenStatusRollbackDialog(false);
    setStatusRollbackPayload(null);
  }, []);

  const handleStatusRollbackConfirm = useCallback(() => {
    const pending = pendingStatusConfirmRef.current;
    if (!pending) return;

    const committed = applyRowUpdate(pending.newRow, pending.oldRow);
    pending.resolve(committed);

    pendingStatusConfirmRef.current = null;
    setOpenStatusRollbackDialog(false);
    setStatusRollbackPayload(null);
  }, [applyRowUpdate]);

  const closeRowDetail = useCallback(() => {
    setOpenDetail(false);
    setDetailRow(null);
  }, []);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        type: 'actions',
        headerName: intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay' }),
        width: 100,
        getActions: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return [];
          }

          return [
            <Tooltip
              key="detail"
              title={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay Göster' })}
              arrow
              placement="top">
              <GridActionsCellItem
                icon={<VisibilityOutlinedIcon sx={{ color: 'primary.main', opacity: 0.85 }} />}
                label={intl.formatMessage({ id: 'common.detail', defaultMessage: 'Detay Göster' })}
                onClick={() => openRowDetail(params.row as EffortItem)}
                showInMenu={false}
              />
            </Tooltip>,
            <PermissionHandler requiredPermissionCodes={['add-activity-board-item']}>
              <Tooltip
                title={intl.formatMessage({ id: 'effortTracing.enterActivity', defaultMessage: 'Aktivite Gir' })}
                arrow
                placement="top">
                <GridActionsCellItem
                  key="add-activity"
                  icon={<AddIcon sx={{ color: 'success.main', opacity: 0.85 }} />}
                  label={intl.formatMessage({ id: 'effortTracing.enterActivity', defaultMessage: 'Aktivite Gir' })}
                  onClick={() => handleAddActivity(params.row as EffortItem)}
                  showInMenu={false}
                />
              </Tooltip>
            </PermissionHandler>,
          ];
        },
      },
      {
        field: 'order',
        headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return null;
          }

          return params.api.getAllRowIds().indexOf(params.id) + 1;
        },
        editable: false,
      },
      {
        field: 'status',
        headerName: intl.formatMessage({ id: 'effortTracing.status', defaultMessage: 'Statü' }),
        headerAlign: 'center',
        align: 'center',
        type: 'singleSelect',
        valueOptions: () => STATUS_OPTIONS.map((x) => x.value),
        getOptionLabel: (value) => STATUS_OPTIONS.find((x) => x.value === value)?.label ?? String(value),
        valueFormatter: (value) => STATUS_OPTIONS.find((x) => x.value === value)?.label ?? '',
        renderCell: (params) => {
          if (isAutogeneratedRow(params.row)) {
            return null;
          }

          const status = Number(params.value ?? params.row?.status ?? EFFORT_STATUS_TYPE_ENUM.IN_SALES);
          return <StatusChip status={status} showNextHint />;
        },
        editable: canAnyUpdate,
        minWidth: 200,
      },
      {
        field: 'consultantId',
        headerName: intl.formatMessage({
          id: 'effortTracing.consultantEmail',
          defaultMessage: 'Talebi Açan Danışman',
        }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => users.find((user) => user.id === value)?.nameSurname || '',
        minWidth: 200,
      },
      {
        field: 'appointedConsultantId',
        headerName: intl.formatMessage({
          id: 'effortTracing.appointedConsultantEmail',
          defaultMessage: 'Atanan Danışman',
        }),
        headerAlign: 'center',
        align: 'center',
        editable: canEditFieldByPermission(permissionCodes, 'appointedConsultantId'),
        minWidth: 200,
        valueGetter: (value) => users.find((u) => u.id === value)?.nameSurname || '',
        renderEditCell: (params) => <AppointedConsultantEditCell {...params} users={users} />,
      },
      {
        field: 'createdAt',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'effortTracing.createdAt', defaultMessage: 'Oluşturulma Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        minWidth: 150,
        editable: false,
      },
      {
        field: 'trackNo',
        headerName: intl.formatMessage({ id: 'effortTracing.uniqueKey', defaultMessage: 'Efor Takip No' }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 300,
        editable: false,
      },
      {
        field: 'bookId',
        headerName: intl.formatMessage({ id: 'effortTracing.bookId', defaultMessage: 'Proje Seçimi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => books.find((book) => book.id === value)?.companyName || '',
        minWidth: 200,
        editable: false,
      },
      {
        field: 'companyVkn',
        headerName: intl.formatMessage({ id: 'effortTracing.companyTitle', defaultMessage: 'Talep Eden Şirket' }),
        headerAlign: 'center',
        align: 'center',
        editable: canEditFieldByPermission(permissionCodes, 'companyVkn'),
        minWidth: 240,
        valueGetter: (value, row) => {
          const book = books.find((b) => b.id === row.bookId);
          if (!book) return value ?? '';

          const company = book.companies?.find((c) => c.vkn === row.companyVkn);

          return company ? company.title : row.companyVkn || '';
        },
        valueSetter: (value, row) => {
          return { ...row, companyVkn: value };
        },
        renderEditCell: (params) => {
          const row = params.row as EffortItem;
          const book = books.find((book) => book.id === row.bookId);

          const options =
            book?.companies?.map((company) => ({
              vkn: company.vkn,
              title: company.title,
            })) ?? [];

          return <CompanyVknEditCell {...params} value={row.companyVkn} options={options} />;
        },
      },
      {
        field: 'projectId',
        headerName: intl.formatMessage({ id: 'effortTracing.projectId', defaultMessage: 'İlgili Ürün' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => products.find((product) => product.id === value)?.title || '',
        minWidth: 200,
        editable: false,
      },
      {
        field: 'explanation',
        headerName: intl.formatMessage({ id: 'effortTracing.explanation', defaultMessage: 'Efor Açıklaması' }),
        minWidth: 200,
        editable: false,
      },
      {
        field: 'effortDay',
        headerName: intl.formatMessage({ id: 'effortTracing.effortDay', defaultMessage: 'Efor Günü' }),
        headerAlign: 'center',
        align: 'center',
        editable: false,
        type: 'number',
      },
      {
        field: 'customerFullName',
        headerName: intl.formatMessage({
          id: 'effortTracing.customerFullName',
          defaultMessage: 'Talep Eden Müşteri Ad Soyad',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 200,
        editable: false,
      },
      {
        field: 'customerEmail',
        headerName: intl.formatMessage({
          id: 'effortTracing.customerEmail',
          defaultMessage: 'Talep Eden Müşteri E-Posta',
        }),
        headerAlign: 'center',
        align: 'center',
        minWidth: 200,
        editable: false,
      },
      {
        field: 'relatedContacts',
        headerName: intl.formatMessage({ id: 'effortTracing.relatedContactEmails', defaultMessage: 'İlgili Kişiler' }),
        headerAlign: 'center',
        align: 'center',
        editable: false,
        minWidth: 180,
        sortable: false,
        renderCell: SummaryCell({ mode: 'relatedContacts' }),
        valueGetter: (_value, row) => {
          const book = books.find((book) => book.id === row.bookId);
          if (!book) return '';

          const filteredRelatedContacts = book.relatedContacts?.filter((relatedContact) =>
            row.relatedContacts?.includes(relatedContact.email)
          );
          return filteredRelatedContacts ?? [];
        },
      },
      {
        field: 'estimatedStartDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'effortTracing.estimatedStartDate', defaultMessage: 'Tahmini Başlangıç' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        editable: false,
        minWidth: 150,
      },
      {
        field: 'estimatedEndDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'effortTracing.estimatedEndDate', defaultMessage: 'Tahmini Bitiş' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        editable: false,
        minWidth: 150,
      },
      {
        field: 'confirmationDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'effortTracing.confirmationDate', defaultMessage: 'Onay/Ret Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        minWidth: 150,
        editable: false,
      },
      {
        field: 'consultantNote',
        headerName: intl.formatMessage({ id: 'effortTracing.consultantNote', defaultMessage: 'Danışman Notu' }),
        editable: canEditFieldByPermission(permissionCodes, 'consultantNote'),
        minWidth: 200,
      },
      {
        field: 'isSasNecessary',
        headerName: intl.formatMessage({ id: 'effortTracing.isSasNecessary', defaultMessage: 'SAS Zorunlu mu?' }),
        headerAlign: 'center',
        editable: canEditFieldByPermission(permissionCodes, 'isSasNecessary'),
        type: 'boolean',
        valueGetter: (value: number) => Boolean(value),
      },
      {
        field: 'sasNo',
        headerName: intl.formatMessage({ id: 'effortTracing.sasNo', defaultMessage: 'SAS No' }),
        headerAlign: 'center',
        align: 'center',
        editable: canEditFieldByPermission(permissionCodes, 'sasNo'),
        minWidth: 200,
      },
      {
        field: 'invoiceNote',
        headerName: intl.formatMessage({ id: 'effortTracing.invoiceNote', defaultMessage: 'Fatura Notu' }),
        editable: canEditFieldByPermission(permissionCodes, 'invoiceNote'),
        minWidth: 200,
      },
      {
        field: 'companyVknForInvoice',
        headerName: intl.formatMessage({
          id: 'confirmEffort.companyVknForInvoice',
          defaultMessage: 'Fatura Kesilecek VKN',
        }),
        headerAlign: 'center',
        align: 'center',
        editable: canEditFieldByPermission(permissionCodes, 'companyVknForInvoice'),
        minWidth: 200,
      },
      {
        field: 'invoiceNo',
        headerName: intl.formatMessage({ id: 'effortTracing.invoiceNo', defaultMessage: 'Fatura No' }),
        headerAlign: 'center',
        align: 'center',
        editable: canEditFieldByPermission(permissionCodes, 'invoiceNo'),
        minWidth: 200,
        renderEditCell: (params) => <InvoiceNoEditCell {...params} />,
      },
      {
        field: 'invoiceDate',
        ...dateColumnType,
        headerName: intl.formatMessage({ id: 'effortTracing.invoiceDate', defaultMessage: 'Fatura Tarihi' }),
        headerAlign: 'center',
        align: 'center',
        valueGetter: (value) => (!value ? null : new Date(value)),
        valueParser: (value) => (!value ? null : new Date(value)),
        valueFormatter: (value) => (value ? renderFormattedDate(value) : ''),
        editable: canEditFieldByPermission(permissionCodes, 'invoiceDate'),
        minWidth: 150,
      },
    ],
    [intl, users, books, products, STATUS_OPTIONS, permissionCodes, openRowDetail, handleAddActivity, canAnyUpdate]
  );

  const handleCloseCreateEffortRequestDialog = () => setOpenCreateEffortRequestDialog(false);
  const handleClickOpenCreateEffortRequestDialog = () => setOpenCreateEffortRequestDialog(true);

  const handleCloseDeleteEffortRequestDialog = () => setOpenDeleteEffortRequestDialog(false);
  const handleClickOpenDeleteEffortRequestDialog = () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    setOpenDeleteEffortRequestDialog(true);
  };

  const discardChanges = useCallback(() => {
    if (!hasUnsavedRows || isSaving) {
      enqueueSnackbar(
        <FormattedMessage id="validations.noUpdatedData" defaultMessage="Henüz hiç bir veri değiştirilmedi!" />,
        { variant: 'warning' }
      );
      return;
    }

    setHasUnsavedRows(false);
    apiRef.current?.updateRows(Object.values(unsavedChangesRef.current.rowsBeforeChange));

    unsavedChangesRef.current = { unsavedRows: {}, rowsBeforeChange: {} };
  }, [apiRef, hasUnsavedRows, isSaving, enqueueSnackbar]);

  const getUsers = async () => {
    try {
      const response = await apiService.getUsers();

      if (response.isSuccess) setUsers(response.data);
    } catch {}
  };

  const getBooks = async () => {
    try {
      const response = await apiService.getBooks();

      if (response.isSuccess) {
        const sortedData = [...response.data].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || ''));
        setBooks(sortedData);
      }
    } catch {}
  };

  const getActivityTypes = async () => {
    try {
      const response = await apiService.getActivityTypes();
      if (response.isSuccess) setActivityTypes(response.data);
    } catch {}
  };

  const getEfforts = useCallback(async () => {
    const appointedConsultantId = showOnlyAssignedToMe
      ? users.find((user) => user.nameSurname === Authentication.getLoggedUser())?.id || null
      : null;

    const postData: GetEffortRequestsFormParams = {
      statusType,
      appointedConsultantId,
    };

    try {
      setLoading(true);

      const response = await apiService.getEfforts(postData);

      if (response.isSuccess) {
        const sortedData = [...response.data].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setEfforts(sortedData);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [statusType, showOnlyAssignedToMe, users]);

  const saveChanges = useCallback(async () => {
    if (!hasUnsavedRows) {
      enqueueSnackbar(
        <FormattedMessage
          id="validations.doubleClickOnData"
          defaultMessage="Lütfen değiştirmek istediğiniz verinin üstüne çift tıklayınız!"
        />,
        { variant: 'warning' }
      );
      return;
    }

    try {
      setIsSaving(true);

      const unsavedRows = Object.values(unsavedChangesRef.current.unsavedRows) as EffortItem[];
      const targetRow = unsavedRows[0];

      const beforeRow = unsavedChangesRef.current.rowsBeforeChange[targetRow.id] as EffortItem | undefined;

      if (!beforeRow) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'effortTracing.missingBeforeRow',
            defaultMessage: 'Kaydetme için önceki satır verisi bulunamadı. Lütfen sayfayı yenileyip tekrar deneyin.',
          }),
          { variant: 'warning' }
        );
        return;
      }

      const from = Number(beforeRow.status) as EFFORT_STATUS_TYPE_ENUM;
      const to = Number(targetRow.status) as EFFORT_STATUS_TYPE_ENUM;

      if (from !== to && !canTransitionStatusByPermission(permissionCodes, from, to)) {
        enqueueSnackbar(
          intl.formatMessage({
            id: 'effortTracing.statusTransitionNotAllowed',
            defaultMessage: 'Bu statü değişikliğine yetkiniz yok.',
          }),
          { variant: 'warning' }
        );
        return;
      }

      const errors = validateEffortBeforeSave(targetRow, intl, permissionCodes);

      if (errors.length) {
        errors.forEach((msg) => enqueueSnackbar(msg, { variant: 'warning' }));
        return;
      }

      const postData = buildUpdatePayload(targetRow, beforeRow, permissionCodes);

      const response = await apiService.updateEffortRequest(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortTracing.updateEffortRequestCompleted"
            defaultMessage="Seçili efor talebini güncelleme işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );

        setHasUnsavedRows(false);
        unsavedChangesRef.current = { unsavedRows: {}, rowsBeforeChange: {} };
        setRowSelectionModel([]);
        await getEfforts();
      }
    } catch {
    } finally {
      setIsSaving(false);
    }
  }, [
    hasUnsavedRows,
    enqueueSnackbar,
    intl,
    permissionCodes,
    validateEffortBeforeSave,
    buildUpdatePayload,
    getEfforts,
  ]);

  const handleResendEmail = async () => {
    if (!rowSelectionModel.length) {
      enqueueSnackbar(<FormattedMessage id="checkbox.noSelection" defaultMessage="Lütfen en az 1 satır seçiniz!" />, {
        variant: 'warning',
      });
      return;
    }

    if (rowSelectionModel.length > 1) {
      enqueueSnackbar(<FormattedMessage id="checkbox.tooSelection" defaultMessage="Birden fazla satır seçilemez!" />, {
        variant: 'warning',
      });
      return;
    }

    try {
      setIsResendingEmail(true);

      const response = await apiService.resendEmail(rowSelectionModel[0] as number);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortTracing.resendEmailSuccessfullyCompleted"
            defaultMessage="Seçili efor talebi için yeniden e-posta gönderimi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
      }
    } catch {
    } finally {
      setIsResendingEmail(false);
    }
  };

  useLayoutEffect(() => {
    if (permissionCodes[0] === STORAGE_KEY_ENUM.NON_INITIALIZED) return;

    if (!permissionCodes.includes('list-effort')) {
      navigate('/dashboard');
    }
  }, [permissionCodes, navigate]);

  useEffect(() => {
    if (!products.length && !isEmptyProducts) {
      dispatch(projectsActions.getProjects());
    }
  }, [products, isEmptyProducts, dispatch]);

  useEffect(() => {
    getUsers();
    getBooks();
    getActivityTypes();
  }, []);

  useEffect(() => {
    getEfforts();
  }, [getEfforts]);

  return (
    <>
      <Grid container justifyContent="flex-start" spacing={1}>
        <PermissionHandler requiredPermissionCodes={['create-effort-request']}>
          {!hasUnsavedRows && (
            <Grid size={2.5}>
              <Button
                onClick={handleClickOpenCreateEffortRequestDialog}
                fullWidth
                size="medium"
                variant="contained"
                endIcon={<AddIcon />}
                color="success"
                sx={{
                  color: 'white',
                  border: 'none',
                  '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
                }}>
                <FormattedMessage id="effortTracing.createEffortRequest" defaultMessage="Efor Talebi Oluştur" />
              </Button>
            </Grid>
          )}
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={[PERM.ADMIN, PERM.INVOICE, PERM.SALES, PERM.USER]}>
          <Grid size={2.5}>
            <Button
              onClick={saveChanges}
              fullWidth
              size="medium"
              variant="contained"
              color="info"
              endIcon={hasUnsavedRows ? <SaveIcon /> : <EditIcon />}>
              <FormattedMessage id="effortTracing.updateEffortRequest" defaultMessage="Efor Talebi Güncelle" />
            </Button>
          </Grid>

          {hasUnsavedRows && (
            <Grid size={2.5}>
              <Button
                onClick={discardChanges}
                fullWidth
                size="medium"
                variant="contained"
                color="primary"
                endIcon={<RestoreIcon />}>
                <FormattedMessage id="effortTracing.discardChanges" defaultMessage="Değişiklikleri İptal Et" />
              </Button>
            </Grid>
          )}
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['delete-effort', 'hard-delete-effort']}>
          {!hasUnsavedRows && (
            <Grid size={2.5}>
              <Button
                onClick={handleClickOpenDeleteEffortRequestDialog}
                fullWidth
                size="medium"
                variant="contained"
                endIcon={<DeleteIcon />}
                color="error">
                <FormattedMessage id="effortTracing.deleteEffortRequest" defaultMessage="Efor Talebi Sil" />
              </Button>
            </Grid>
          )}
        </PermissionHandler>

        <PermissionHandler requiredPermissionCodes={['send-effort-mail']}>
          {!hasUnsavedRows && (
            <Grid size={2.5}>
              <Button
                onClick={handleResendEmail}
                disabled={isResendingEmail}
                fullWidth
                size="medium"
                variant="contained"
                endIcon={<ReplyIcon />}
                color="warning"
                sx={{
                  color: 'white',
                  border: 'none',
                  '&:hover': { backgroundColor: 'rgb(83, 189, 83, 0.8)', border: 'none', boxShadow: 'none' },
                }}>
                <FormattedMessage id="effortTracing.resendEmail" defaultMessage="Yeniden E-Posta Gönder" />
              </Button>
            </Grid>
          )}
        </PermissionHandler>
      </Grid>

      <ChipsArray
        type="effort"
        disabled={hasUnsavedRows}
        statusType={statusType}
        setStatusType={setStatusType}
        showOnlyAssignedToMe={showOnlyAssignedToMe}
        setShowOnlyAssignedToMe={setShowOnlyAssignedToMe}
      />

      <DataGridBox>
        <BulkEditingContext.Provider value={bulkEditingContextValue}>
          <ZebraDataGrid
            pagination
            checkboxSelection
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            rowSelectionModel={rowSelectionModel}
            columns={columns}
            initialState={{
              aggregation: {
                model: {
                  effortDay: 'sum',
                },
              },
            }}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            loading={loading || isSaving}
            localeText={renderDataGridLocaleText()}
            rows={efforts}
            slots={{
              noRowsOverlay: CustomNoRowsOverlay,
              toolbar: CustomToolbar,
            }}
            slotProps={{
              toolbar: {
                exportPremiumExcel: true,
                showTemplates: true,
                apiRef,
                pageName,
              },
              filterPanel: {
                filterFormProps: {
                  logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                  columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                  operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                  valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                },
              },
            }}
            disableRowGrouping
            apiRef={apiRef}
            processRowUpdate={processRowUpdate}
            isCellEditable={isCellEditable}
            onCellEditStart={handleCellEditStart}
            onCellDoubleClick={handleCellDoubleClick}
            sx={{
              [`& .${gridClasses.row}.row--removed`]: {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === 'light') return 'rgba(255, 170, 170, 0.3)';
                  return darken('rgba(255, 170, 170, 1)', 0.7);
                },
              },
              [`& .${gridClasses.row}.row--edited`]: {
                backgroundColor: (theme) => {
                  if (theme.palette.mode === 'light') return 'rgba(255, 254, 176, 0.3)';
                  return darken('rgba(255, 254, 176, 1)', 0.6);
                },
              },
              '& .row--disabled': { opacity: 0.45, filter: 'grayscale(0.6)' },
              '& .row--locked': { opacity: 1, filter: 'none' },
              [`& .${gridClasses.row}.row--edited .${gridClasses.cell}.cell--editableInPolicy`]: {
                boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.15)',
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(120, 190, 255, 0.20)' : 'rgba(120, 190, 255, 0.15)',
              },
              [`& .${gridClasses.row}.row--edited .${gridClasses.cell}.cell--editableInPolicy:hover`]: {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light' ? 'rgba(120, 190, 255, 0.28)' : 'rgba(120, 190, 255, 0.22)',
              },
            }}
            getRowClassName={getRowClassName}
            getCellClassName={(params) => {
              const rowId = params.id as GridRowId;
              const unsavedRow = unsavedChangesRef.current.unsavedRows[rowId];
              if (!unsavedRow) return '';

              const field = params.field as EditableField;
              if (!params.colDef.editable) return '';

              if (field === 'status') {
                return canAnyUpdate ? 'cell--editableInPolicy' : '';
              }

              const canEdit = canEditFieldByPermission(permissionCodes, field);
              return canEdit ? 'cell--editableInPolicy' : '';
            }}
          />
        </BulkEditingContext.Provider>
      </DataGridBox>

      <CreateEffortRequestDialog
        open={openCreateEffortRequestDialog}
        handleClose={handleCloseCreateEffortRequestDialog}
        books={books}
        products={products}
        refresh={getEfforts}
      />
      <DeleteEffortRequestDialog
        open={openDeleteEffortRequestDialog}
        effortIds={rowSelectionModel}
        handleClose={handleCloseDeleteEffortRequestDialog}
        refresh={getEfforts}
        setRowSelectionModel={setRowSelectionModel}
      />
      <StatusRollbackDialog
        open={openStatusRollbackDialog}
        onClose={handleStatusRollbackCancel}
        onConfirm={handleStatusRollbackConfirm}
        statusRollbackPayload={statusRollbackPayload}
      />

      <DetailDrawer
        open={openDetail}
        close={closeRowDetail}
        detailRow={detailRow}
        books={books}
        products={products}
        users={users}
      />

      <CreateActivityBoardItemDialog
        open={openActivityDialog}
        handleClose={() => setOpenActivityDialog(false)}
        books={books}
        products={products}
        activityTypes={filteredActivityTypes}
        initialData={initialActivityData}
        onSuccess={() => navigate('/activity-managing')}
      />
    </>
  );
};

export default EffortTracing;
